import React, { useEffect, useMemo, useRef, useState } from 'react'
import './Clockify.css'
import './JobApplicants.css'
import TeamHeader from '../TeamHeader/TeamHeader'
import LoadingSpinner from '../../backend/components/LoadingSpinner';
import FilterDropdown from "../TeamPortalUtil/FilterDropdown";

// Auth
import { useAuth } from '../../Auth';

// Toast imports
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import {
    PiMagnifyingGlass,
    PiUsers,
    PiUserCircle,
    PiCaretDown,
    PiCaretRight,
    PiClipboardText,
    PiFileText,
} from "react-icons/pi";
import { MdAssignmentInd } from "react-icons/md";
import { FaFlagCheckered } from "react-icons/fa";

// --- Local Development Mode ---
// Set to true to proxy all API calls to localhost:8000
const LOCAL_MODE = false;

function apiUrl(path: string): string {
    return LOCAL_MODE ? `http://localhost:8000${path}` : path;
}

function credentials(): RequestCredentials {
    return LOCAL_MODE ? "include" : "same-origin";
}

/* ─────────────────────────────────────────────────────────── */

interface OrgUser {
    id: number;
    user_id: number;
    username: string;
    email: string;
    first_name: string;
    last_name: string;
}

interface Application {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    phone_number: string;
    specialty: string;
    us_or_pr: boolean;
    resume: string;
    job_id: number;
    job_name: string | null;
    date_applied: string;
    application_status: "Unseen" | "Pending" | "Complete";
    application_complete_date: string;
    application_assigned_to: number | null;
    comments: ApplicationComment[];
}

interface ApplicationComment {
    application: number;
    comment_id: number;
    comment: string;
    comment_date: string;
    comment_creator: number;
    comment_creator_name: string;
}

const STATUS_COLORS: Record<string, string> = {
    Complete: "#20bf55",
    Pending: "#f9a825",
    Unseen: "#e53935",
};

const JobApplicants = () => {

    const { username, first_name, last_name } = useAuth();

    const containerRef = useRef<HTMLDivElement | null>(null);

    function displayNotification(successOrFail: boolean, message: string) {
        if (successOrFail) {
            toast.success(message, {
                autoClose: 4000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                style: {
                    backgroundColor: '#e7ecf1ff',
                    color: 'black',
                    fontSize: '16px',
                }
            });
        } else {
            toast.error(message, {
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                style: {
                    backgroundColor: '#e7ecf1ff',
                    color: 'black',
                    fontSize: '16px',
                }
            });
        }
    }

    const fetchCsrfToken = async () => {
        const response = await fetch(apiUrl('/api/csrf-token/'), {
            method: 'GET',
            credentials: credentials(),
        });

        if (response.ok) {
            const data = await response.json();
            return data.csrfToken;
        } else {
            console.error('Failed to fetch CSRF token');
            return null;
        }
    };

    const formatDate = (dateString?: string, timeOnSecondLine = true) => {
        if (!dateString) return "N/A";

        const date = new Date(dateString);
        if (isNaN(date.getTime())) return "N/A";

        const formattedDate = date.toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "numeric",
            timeZone: "UTC",
        });

        const formattedTime = date.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
            timeZone: "UTC",
        });

        if (timeOnSecondLine) {
            return (
                <div style={{ display: "flex", flexDirection: "column", lineHeight: "1.2" }}>
                    <span>{formattedDate}</span>
                    <span>{formattedTime}</span>
                </div>
            );
        }

        return (
            <span>
                {formattedDate}, {formattedTime}
            </span>
        );
    };

    /* ---------------- FNN Users (for assignment) ---------------- */

    const [fnnUsers, setFnnUsers] = useState<OrgUser[]>([]);

    const fetchFNNUsers = async () => {
        try {
            const response = await fetch(apiUrl('/api/inventory/fnn-users/'), {
                credentials: credentials(),
            });
            const data: OrgUser[] = await response.json();

            if (response.ok) {
                setFnnUsers(data);
            } else {
                console.error("Failed to load FNN users:", (data as any).error || response.statusText);
            }
        } catch (error) {
            console.error("Error fetching FNN users:", error);
        }
    };

    useEffect(() => {
        fetchFNNUsers();
    }, []);

    const getUserById = (id: number | null): OrgUser | null => {
        if (!id) return null;
        return fnnUsers.find(user => user.id === id) || null;
    };

    const findUserByNameOrEmail = (input: string): OrgUser | null => {
        const normalized = input.toLowerCase().trim();
        return fnnUsers.find(user =>
            `${user.first_name} ${user.last_name}`.toLowerCase() === normalized ||
            user.email.toLowerCase() === normalized
        ) || null;
    };

    const loggedFnnUserId = React.useMemo(() => {
        if (!fnnUsers?.length) return null;

        const matchedUser = fnnUsers.find(
            (user) =>
                user.username === username &&
                user.first_name === first_name &&
                user.last_name === last_name
        );

        return matchedUser ? matchedUser.id : null;
    }, [fnnUsers, username, first_name, last_name]);

    /* ---------------- Applications ---------------- */

    const [applications, setApplications] = useState<Application[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [search, setSearch] = useState("");
    const [expandedRowId, setExpandedRowId] = useState<number | null>(null);

    const [assigneeSearch, setAssigneeSearch] = useState<Record<number, string>>({});
    const [assigneeFocused, setAssigneeFocused] = useState<number | null>(null);

    const [showAssignModal, setShowAssignModal] = useState(false);
    const [selectedApplication, setSelectedApplication] = useState<Application | null>(null);
    const [selectedAssignee, setSelectedAssignee] = useState<OrgUser | null>(null);
    const [isAssigning, setIsAssigning] = useState(false);

    const [showCompleteModal, setShowCompleteModal] = useState(false);
    const [applicationToComplete, setApplicationToComplete] = useState<Application | null>(null);
    const [isCompleting, setIsCompleting] = useState(false);

    const [newComments, setNewComments] = useState<Record<number, string>>({});

    const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
    const availableStatuses = ["Unseen", "Pending", "Complete"];

    const [selectedJobs, setSelectedJobs] = useState<string[]>([]);

    const availableJobs = useMemo(() => {
        const jobNames = applications.map(
            (application) => application.job_name || "General Application"
        );
        return Array.from(new Set(jobNames)).sort();
    }, [applications]);

    const fetchApplications = async () => {
        try {
            setLoading(true);
            setError(null);

            const res = await fetch(apiUrl("/api/application/list/"), {
                credentials: credentials(),
            });

            if (!res.ok) throw new Error("Failed to fetch applications");

            const data: Application[] = await res.json();
            setApplications(data);
        } catch (err) {
            console.error('Failed fetching applications:', err);
            setError(err instanceof Error ? err.message : "Error loading applications");
            setApplications([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchApplications();
    }, []);

    const toggleRow = (id: number) => {
        setExpandedRowId(prev => (prev === id ? null : id));
    };

    const assignApplication = async () => {
        if (!selectedApplication || !selectedAssignee) return;

        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return;

        try {
            setIsAssigning(true);

            const res = await fetch(
                apiUrl("/api/application/update-status/"),
                {
                    method: "PUT",
                    credentials: credentials(),
                    headers: {
                        "Content-Type": "application/json",
                        "X-CSRFToken": csrfToken,
                    },
                    body: JSON.stringify({
                        application_id: selectedApplication.id,
                        status: "Pending",
                        assigned_user_id: selectedAssignee.user_id,
                    }),
                }
            );

            if (!res.ok) throw new Error("Assignment failed");

            await fetchApplications();
            displayNotification(true, "Applicant assigned successfully!");
            setShowAssignModal(false);
        } catch {
            displayNotification(false, "Failed to assign applicant");
        } finally {
            setIsAssigning(false);
        }
    };

    const markApplicationComplete = async () => {
        if (!applicationToComplete) return;

        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return;

        try {
            setIsCompleting(true);

            const res = await fetch(
                apiUrl("/api/application/update-status/"),
                {
                    method: "PUT",
                    credentials: credentials(),
                    headers: {
                        "Content-Type": "application/json",
                        "X-CSRFToken": csrfToken,
                    },
                    body: JSON.stringify({
                        application_id: applicationToComplete.id,
                        status: "Complete",
                    }),
                }
            );

            if (!res.ok) throw new Error();

            await fetchApplications();
            displayNotification(true, "Applicant marked as reviewed");
            setShowCompleteModal(false);
        } catch {
            displayNotification(false, "Failed to complete review");
        } finally {
            setIsCompleting(false);
        }
    };

    const addComment = async (application: Application, commentText: string) => {
        if (!loggedFnnUserId) return;

        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return;

        try {
            const res = await fetch(
                apiUrl("/api/application/comment-post/"),
                {
                    method: "POST",
                    credentials: credentials(),
                    headers: {
                        "Content-Type": "application/json",
                        "X-CSRFToken": csrfToken,
                    },
                    body: JSON.stringify({
                        application: application.id,
                        comment: commentText,
                        comment_creator: loggedFnnUserId,
                    }),
                }
            );

            if (!res.ok) throw new Error("Posting comment failed");

            await fetchApplications();
            displayNotification(true, "Comment added successfully!");
        } catch {
            displayNotification(false, "Failed to add comment");
        }
    };

    const handlePostComment = async (application: Application) => {
        const commentText = newComments[application.id]?.trim();
        if (!commentText) return;

        await addComment(application, commentText);

        setNewComments(prev => ({
            ...prev,
            [application.id]: "",
        }));
    };

    // Close the expanded row / assignee autocomplete when clicking outside,
    // unless a modal is open (those handle their own dismissal).
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(event.target as Node)
            ) {
                if (showAssignModal || showCompleteModal) return;

                setExpandedRowId(null);
                setAssigneeFocused(null);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [showAssignModal, showCompleteModal]);

    const filteredApplications = useMemo(() => {
        const q = search.toLowerCase();
        return applications.filter(application => {
            const nameMatch =
                `${application.first_name} ${application.last_name}`.toLowerCase().includes(q) ||
                application.email.toLowerCase().includes(q);

            const statusMatch =
                selectedStatuses.length === 0 ||
                selectedStatuses.includes(application.application_status);

            const jobMatch =
                selectedJobs.length === 0 ||
                selectedJobs.includes(application.job_name || "General Application");

            return nameMatch && statusMatch && jobMatch;
        });
    }, [search, applications, selectedStatuses, selectedJobs]);

    const reviewedCount = applications.filter(a => a.application_status === "Complete").length;

    return (
        <div className="customer-directory-body">

            <TeamHeader title="Human Resources" />
            <ToastContainer />

            <div className="support-container" ref={containerRef}>

                <div className="support-page-header">
                    <div>
                        <h1 className="support-page-title">
                            Applicants
                        </h1>
                        <p className="support-page-sub">
                            Review and manage inbound job applications.
                        </p>
                    </div>
                </div>

                {/* Stats */}

                <div className="account-stats">

                    <div className="account-stat-card">
                        <PiUsers />
                        <span>{applications.length}</span>
                        <small>Total Applicants</small>
                    </div>

                    <div className="account-stat-card">
                        <PiClipboardText />
                        <span>{reviewedCount}/{applications.length}</span>
                        <small>Reviewed</small>
                    </div>

                </div>

                {/* Toolbar */}

                <div className="account-toolbar ja-toolbar">
                    <div className="account-search">
                        <PiMagnifyingGlass />
                        <input
                            placeholder="Search by name or email..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>

                    <FilterDropdown
                        dropdownClassName="marketing-contact-dropdown"
                        sections={[
                            { key: "status", title: "Status", options: availableStatuses },
                            { key: "job", title: "Job", options: availableJobs },
                        ]}
                        onChange={(filters) => {
                            setSelectedStatuses(filters.status || []);
                            setSelectedJobs(filters.job || []);
                        }}
                    />
                </div>

                {loading && (
                    <p className="support-page-sub">Loading applicants...</p>
                )}

                {!loading && error && (
                    <p className="support-page-sub">{error}</p>
                )}

                {!loading && !error && (

                    <div className="account-user-list">

                        {filteredApplications.map(application => {

                            const expanded = expandedRowId === application.id;
                            const statusColor = STATUS_COLORS[application.application_status] || "inherit";

                            return (
                                <div
                                    key={application.id}
                                    className={`account-user-card ${expanded ? "expanded" : ""}`}
                                >

                                    <div
                                        className="account-user-header"
                                        onClick={() => toggleRow(application.id)}
                                    >

                                        <div className="account-user-left">

                                            <div className="account-avatar">
                                                <PiUserCircle />
                                            </div>

                                            <div className='account-user-left-name'>
                                                <div className="account-user-name-row">
                                                    <h3>{application.first_name} {application.last_name}</h3>
                                                </div>
                                                <p>
                                                    {application.job_name
                                                        ? `Job #${application.job_id} — ${application.job_name}`
                                                        : `Job #${application.job_id} — General Application`}
                                                </p>
                                            </div>

                                        </div>

                                        <div className="account-user-summary">

                                            <div
                                                className="ja-status-pill"
                                                style={{
                                                    color: statusColor,
                                                    backgroundColor: `${statusColor}1A`, // ~10% opacity tint of the status color
                                                    border: `1px solid ${statusColor}40`, // ~25% opacity border
                                                }}
                                            >
                                                <span
                                                    className="ja-status-pill-dot"
                                                    style={{ backgroundColor: statusColor }}
                                                />
                                                {application.application_status}
                                            </div>

                                            <div className="hours-pill">
                                                {formatDate(application.date_applied, false)}
                                            </div>

                                            {expanded ? (
                                                <PiCaretDown className="expand-icon" />
                                            ) : (
                                                <PiCaretRight className="expand-icon" />
                                            )}

                                        </div>

                                    </div>

                                    {expanded && (
                                        <div className="onboarding-section" onClick={(e) => e.stopPropagation()}>

                                            <div className="onboarding-section-header">
                                                <PiClipboardText />
                                                <span>Applicant Details</span>
                                            </div>

                                            <div className="ja-detail-grid">

                                                <div className="ja-detail-item">
                                                    <strong>Email</strong> {application.email}
                                                </div>

                                                <div className="ja-detail-item">
                                                    <strong>Phone</strong> {application.phone_number}
                                                </div>

                                                <div className="ja-detail-item">
                                                    <strong>US Citizen / PR</strong> {application.us_or_pr ? "Yes" : "No"}
                                                </div>

                                                <div className="ja-detail-item">
                                                    <strong>Specialty</strong> {application.specialty}
                                                </div>

                                                <div className="ja-detail-item">
                                                    <strong>Resume</strong>{" "}
                                                    <a href={application.resume} target="_blank" rel="noreferrer" className="ja-resume-link">
                                                        <PiFileText /> View Resume
                                                    </a>
                                                </div>

                                            </div>

                                            <div className="ja-comments-block">

                                                <div className="ja-detail-grid">

                                                    <div className="ja-detail-item ja-assignee-item">
                                                        <strong>Assigned To</strong>

                                                        {application.application_status === "Complete" ? (
                                                            <div className="ja-assignee-readonly">
                                                                {application.application_assigned_to
                                                                    ? `${getUserById(application.application_assigned_to)?.first_name} ${getUserById(application.application_assigned_to)?.last_name}`
                                                                    : '—'}
                                                            </div>
                                                        ) : (
                                                            <>
                                                                <input
                                                                    type="text"
                                                                    className="ja-assignee-input"
                                                                    value={
                                                                        assigneeSearch[application.id] ??
                                                                        (application.application_assigned_to
                                                                            ? `${getUserById(application.application_assigned_to)?.first_name} ${getUserById(application.application_assigned_to)?.last_name}`
                                                                            : "")
                                                                    }
                                                                    placeholder="Click to assign or type name/email"
                                                                    onFocus={() => setAssigneeFocused(application.id)}
                                                                    onChange={(e) => {
                                                                        setAssigneeSearch(prev => ({
                                                                            ...prev,
                                                                            [application.id]: e.target.value,
                                                                        }));
                                                                        setAssigneeFocused(application.id);
                                                                    }}
                                                                    onBlur={() => {
                                                                        setTimeout(() => setAssigneeFocused(null), 200);
                                                                    }}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === "Enter") {
                                                                            const user = findUserByNameOrEmail(assigneeSearch[application.id] || "");
                                                                            if (!user) {
                                                                                displayNotification(false, "Invalid contact");
                                                                                return;
                                                                            }
                                                                            setSelectedApplication(application);
                                                                            setSelectedAssignee(user);
                                                                            setShowAssignModal(true);
                                                                            setAssigneeFocused(null);
                                                                        } else if (e.key === "Escape") {
                                                                            setAssigneeFocused(null);
                                                                        }
                                                                    }}
                                                                />

                                                                {(assigneeFocused === application.id && fnnUsers.length > 0) && (
                                                                    <div className="ja-autocomplete-list">
                                                                        {fnnUsers
                                                                            .filter(user =>
                                                                                `${user.first_name} ${user.last_name}`.toLowerCase().includes((assigneeSearch[application.id] || "").toLowerCase())
                                                                            )
                                                                            .map(user => (
                                                                                <div
                                                                                    key={user.user_id}
                                                                                    className="ja-autocomplete-item"
                                                                                    onMouseDown={(e) => {
                                                                                        e.preventDefault();
                                                                                        const fullName = `${user.first_name} ${user.last_name}`;
                                                                                        setAssigneeSearch(prev => ({
                                                                                            ...prev,
                                                                                            [application.id]: fullName,
                                                                                        }));
                                                                                        setSelectedApplication(application);
                                                                                        setSelectedAssignee(user);
                                                                                        setShowAssignModal(true);
                                                                                        setAssigneeFocused(null);
                                                                                    }}
                                                                                >
                                                                                    {user.first_name} {user.last_name} ({user.email})
                                                                                </div>
                                                                            ))}
                                                                    </div>
                                                                )}
                                                            </>
                                                        )}
                                                    </div>

                                                    <div className="ja-detail-item">
                                                        <strong>Completed At</strong> {formatDate(application.application_complete_date)}
                                                    </div>

                                                </div>

                                                <strong>Internal Comments</strong>

                                                <div className="ja-add-comment-row">
                                                    <input
                                                        type="text"
                                                        placeholder="Add a comment..."
                                                        value={newComments[application.id] || ""}
                                                        onChange={(e) =>
                                                            setNewComments(prev => ({
                                                                ...prev,
                                                                [application.id]: e.target.value,
                                                            }))
                                                        }
                                                        onKeyDown={async (e) => {
                                                            if (e.key === "Enter") {
                                                                e.preventDefault();
                                                                await handlePostComment(application);
                                                            }
                                                        }}
                                                    />
                                                    <button
                                                        className="btn-primary"
                                                        onClick={() => handlePostComment(application)}
                                                    >
                                                        Post
                                                    </button>
                                                </div>

                                                {application.comments.length === 0 ? (
                                                    <p className="ja-no-comments">No comments yet</p>
                                                ) : (
                                                    <ul className="ja-comments-list">
                                                        {application.comments.map((comment) => (
                                                            <li key={comment.comment_id} className="ja-comment-item">
                                                                <div className="ja-comment-header">
                                                                    <strong>{comment.comment_creator_name}</strong>
                                                                    <span>{formatDate(comment.comment_date, false)}</span>
                                                                </div>
                                                                <div>{comment.comment}</div>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )}
                                            </div>

                                            {application.application_status !== "Complete" && (
                                                <div className="account-actions clockify-user-actions">
                                                    <button
                                                        className="btn-primary"
                                                        onClick={() => {
                                                            setApplicationToComplete(application);
                                                            setShowCompleteModal(true);
                                                        }}
                                                    >
                                                        Mark as Reviewed
                                                    </button>
                                                </div>
                                            )}

                                        </div>
                                    )}

                                </div>
                            );

                        })}

                        {applications.length === 0 && !loading && (
                            <p className="support-page-sub">No applicants found.</p>
                        )}

                    </div>

                )}

            </div>

            {/* Assign Application Modal */}
            {showAssignModal && selectedApplication && selectedAssignee && (
                <div
                    className="modal-overlay"
                    onClick={() => {
                        if (!isAssigning) setShowAssignModal(false);
                    }}
                >
                    <div
                        className="modal-box account-password-modal"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {isAssigning ? (
                            <div className="ja-modal-loading">
                                <LoadingSpinner />
                            </div>
                        ) : (
                            <>
                                <div className="modal-header">
                                    <h3 className="modal-title">
                                        <MdAssignmentInd style={{ marginRight: 8 }} />
                                        Assign Applicant
                                    </h3>
                                </div>

                                <div className="modal-body">
                                    <p className="support-page-sub">
                                        You are about to assign the responsibility to review{" "}
                                        <strong>{selectedApplication.first_name} {selectedApplication.last_name}</strong>
                                        {selectedApplication.job_name && (
                                            <> for <strong>{selectedApplication.job_name}</strong></>
                                        )}.
                                    </p>

                                    <p style={{ marginTop: "1rem" }}>
                                        <strong>To:</strong><br />
                                        {selectedAssignee.first_name} {selectedAssignee.last_name} ({selectedAssignee.email})
                                    </p>

                                    <p className="ja-assign-note">
                                        The assignee will be notified via email.
                                    </p>
                                </div>

                                <div className="modal-actions">
                                    <button
                                        className="btn-ghost"
                                        onClick={() => {
                                            const previousValue = selectedApplication?.application_assigned_to
                                                ? `${getUserById(selectedApplication.application_assigned_to)?.first_name} ${getUserById(selectedApplication.application_assigned_to)?.last_name}`
                                                : "";

                                            setAssigneeSearch(prev => ({
                                                ...prev,
                                                [selectedApplication!.id]: previousValue,
                                            }));

                                            setShowAssignModal(false);
                                            setSelectedAssignee(null);
                                            setSelectedApplication(null);
                                        }}
                                    >
                                        Cancel
                                    </button>

                                    <button
                                        className="btn-primary"
                                        onClick={assignApplication}
                                    >
                                        Confirm
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Complete Application Modal */}
            {showCompleteModal && applicationToComplete && (
                <div
                    className="modal-overlay"
                    onClick={() => {
                        if (!isCompleting) setShowCompleteModal(false);
                    }}
                >
                    <div
                        className="modal-box account-password-modal"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {isCompleting ? (
                            <div className="ja-modal-loading">
                                <LoadingSpinner />
                            </div>
                        ) : (
                            <>
                                <div className="modal-header">
                                    <h3 className="modal-title">
                                        <FaFlagCheckered style={{ marginRight: 8 }} />
                                        Mark Applicant as Reviewed
                                    </h3>
                                </div>

                                <div className="modal-body">
                                    <p className="support-page-sub">
                                        You are about to mark this applicant as reviewed:
                                    </p>

                                    <p style={{ marginTop: "1rem" }}>
                                        <strong>Applicant:</strong><br />
                                        {applicationToComplete.first_name} {applicationToComplete.last_name}
                                    </p>

                                    <p style={{ marginTop: "0.5rem" }}>
                                        <strong>Position:</strong><br />
                                        {applicationToComplete.job_name || `Job #${applicationToComplete.job_id}`}
                                    </p>

                                    <p className="ja-assign-note">
                                        This action will mark the applicant as reviewed and update the completion date.
                                    </p>
                                </div>

                                <div className="modal-actions">
                                    <button
                                        className="btn-ghost"
                                        onClick={() => {
                                            setShowCompleteModal(false);
                                            setApplicationToComplete(null);
                                        }}
                                    >
                                        Cancel
                                    </button>

                                    <button
                                        className="btn-primary"
                                        onClick={markApplicationComplete}
                                    >
                                        Mark as Reviewed
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

        </div>
    )
}

export default JobApplicants